import React, { useEffect, useMemo, useState } from "react";
import { Page } from "@/components/Layout";
import { api } from "@/lib/api";
import LegalToc, { slugifyHeading } from "@/components/LegalToc";
import { renderMarkdown } from "@/lib/markdown";

const HERO_COPY = {
  privacy: {
    eyebrow: "Legal · Privacy",
    heading: "Privacy",
    accent: "policy",
    lead: "How MindDhara collects, uses and safeguards your personal information across our services.",
  },
  terms: {
    eyebrow: "Legal · Terms",
    heading: "Terms &",
    accent: "conditions",
    lead: "The agreement that governs your use of the MindDhara platform, therapy, mentorship and shop.",
  },
  cookie: {
    eyebrow: "Legal · Cookies",
    heading: "Cookie",
    accent: "policy",
    lead: "What cookies we use, why we use them and how you can control them at any time.",
  },
};

export default function LegalPage({ kind }) {
  const [content, setContent] = useState("");
  useEffect(() => {
    const fieldKey = kind === "privacy" ? "privacy_policy" : kind === "terms" ? "terms_and_conditions" : "cookie_policy";
    api.get("/settings/public").then((r) => setContent(r.data?.[fieldKey] || ""));
  }, [kind]);

  const hero = HERO_COPY[kind] || HERO_COPY.privacy;

  // TOC items derived from H2 headings in the markdown.
  const tocItems = useMemo(
    () => content.split("\n")
      .map((l) => l.trim())
      .filter((l) => l.startsWith("## "))
      .map((l) => ({ id: slugifyHeading(l.slice(3)), label: l.slice(3) })),
    [content],
  );

  return (
    <Page>
      <section className="md-section md-page-hero" data-testid={`legal-hero-${kind}`}>
        <div className="md-container text-center">
          <div className="md-label-eyebrow mb-3">{hero.eyebrow}</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif" data-testid={`legal-hero-title-${kind}`}>
            {hero.heading} <em>{hero.accent}</em>
          </h1>
          <p className="text-base mt-5 max-w-2xl mx-auto">{hero.lead}</p>
        </div>
      </section>

      <section className="md-section">
        <div className="md-container">
          <div className="md-legal-layout">
            <article className="md-legal-article md-legal-article-flat" data-testid={`legal-article-${kind}`}>
              <div className="md-legal-body" data-testid={`legal-${kind}`}>{renderMarkdown(content)}</div>
              {kind === "cookie" && (
                <button onClick={() => window.dispatchEvent(new Event("md:open-cookie-settings"))} className="btn-outline mt-8" data-testid="legal-cookie-settings">Open Cookie Settings</button>
              )}
            </article>
            <LegalToc items={tocItems} testid={`legal-toc-${kind}`} />
          </div>
        </div>
      </section>
    </Page>
  );
}
